import { Platform } from 'react-native';
import * as RNLocalize from 'react-native-localize';
import { PurchasesStoreProduct } from 'react-native-purchases';

import { RevenueCatOffering, RevenueCatPackageInfo } from '../services/revenueCatService';

import { REVENUECAT_PRODUCT_IDS } from './revenueCatProducts';

type ProductPriceOverride = {
  price: number;
  priceString: string;
  currencyCode: string;
};

const GLOBAL_PRICE_OVERRIDES: Record<string, ProductPriceOverride> = {
  [REVENUECAT_PRODUCT_IDS.PREMIUM_ANNUAL]: {
    price: 39.99,
    priceString: 'US$39.99',
    currencyCode: 'USD',
  },
};

const IOS_STOREFRONT_PRICE_OVERRIDES: Record<string, Record<string, ProductPriceOverride>> = {
  AU: {
    [REVENUECAT_PRODUCT_IDS.PREMIUM_MONTHLY]: {
      price: 7.99,
      priceString: '$7.99',
      currencyCode: 'AUD',
    },
    [REVENUECAT_PRODUCT_IDS.PREMIUM_ANNUAL]: {
      price: 39.99,
      priceString: 'US$39.99',
      currencyCode: 'USD',
    },
  },
};

const normalizeCountryCode = (countryCode: string): string => {
  const upperCode = countryCode.toUpperCase();
  if (upperCode === 'AUS') {
    return 'AU';
  }

  return upperCode;
};

const getPriceOverride = (
  productId: string,
  storefrontCountryCode: string | null | undefined,
): ProductPriceOverride | null => {
  const globalOverride = GLOBAL_PRICE_OVERRIDES[productId];
  if (globalOverride) {
    return globalOverride;
  }

  if (Platform.OS !== 'ios') {
    return null;
  }

  const deviceCountryCode = RNLocalize.getCountry();
  const deviceTimeZone = RNLocalize.getTimeZone?.();
  const countryCodes = [
    storefrontCountryCode,
    deviceTimeZone?.startsWith('Australia/') ? 'AU' : null,
    deviceCountryCode,
  ].filter(Boolean) as string[];

  for (const countryCode of countryCodes) {
    const override = IOS_STOREFRONT_PRICE_OVERRIDES[normalizeCountryCode(countryCode)]?.[productId];
    if (override) {
      return override;
    }
  }

  return null;
};

const applyProductPriceOverride = <T extends { identifier: string; price?: number; priceString?: string; currencyCode?: string }>(
  product: T,
  storefrontCountryCode: string | null | undefined,
): T => {
  const override = getPriceOverride(product.identifier, storefrontCountryCode);
  if (!override) {
    return product;
  }

  return {
    ...product,
    price: override.price,
    priceString: override.priceString,
    currencyCode: override.currencyCode,
  };
};

export const applyStorefrontPricingToOffering = (
  offering: RevenueCatOffering | null,
  storefrontCountryCode: string | null | undefined,
): RevenueCatOffering | null => {
  if (!offering) {
    return offering;
  }

  return {
    ...offering,
    packages: offering.packages.map((pkg: RevenueCatPackageInfo) => ({
      ...pkg,
      product: applyProductPriceOverride(pkg.product, storefrontCountryCode),
    })),
  };
};

export const applyStorefrontPricingToProducts = (
  products: PurchasesStoreProduct[],
  storefrontCountryCode: string | null | undefined,
): PurchasesStoreProduct[] => {
  return products.map((product) => applyProductPriceOverride(product, storefrontCountryCode) as PurchasesStoreProduct);
};
